import { AppConfig } from '@dh-io-emh/config-types';
import { addTrackingReducerConnection, getTrackingDispatchers } from '@owc/connection-aem-tracking';
import { MediaQueryService } from '@workbench/core';
import { AuthService } from '../auth';
import { Logger } from '../utils/logger.util';
import {
  TrackingApplication,
  TrackingEvent,
  TrackingGlobalEvent,
  TrackingLinkClickEvent,
  TrackingPageViewEvent,
} from './tracking.types';

export type TrackingService = ReturnType<typeof createTrackingService>;

const aemTrackEvent = async (event: TrackingEvent) =>
  getTrackingDispatchers().then((dispatcher) => {
    dispatcher.pushTrackingEvent(event);
  });

/**
 * Wait for cscsUserContext to be available, otherwise reject after timeout.
 *
 * @param timeout {number} - timeout in ms
 * @returns {Promise<'b2c' | 'b2b'>} - Promise that resolves with the user context or rejects with an error
 */
export const waitForCscsUserContext = (timeout = 5000): Promise<'b2c' | 'b2b'> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const getContext = () => (window as any).cscsUserContext?.toLowerCase();
    const checkContext = () => {
      const context = getContext();

      if (context) resolve(context);

      if (Date.now() - startTime > timeout) reject(new Error('Timeout while waiting for cscsUserContext'));

      setTimeout(checkContext, 150);
    };

    checkContext();
  });
};

export const createTrackingService = ({
  config,
  authService,
  appVersion,
}: {
  config: AppConfig;
  authService: AuthService;
  appVersion: string;
}) => {
  // ensure global dataLayer_ow exists because gtm is loaded async
  window.dataLayer_ow ||= [];

  const { tracking, shopCountry, lang, currency } = config;

  const defaultApplication = {
    id: tracking.id,
    subId: tracking.subId,
    type: tracking.assortmentType,
    version: appVersion,
    // Only within AEM we need to provide the parentApplicationId
    parentApplicationId: 'aem',
  };

  // Ensure tracking connection is added
  // (relevant for local development, AEM live environments are adding this connection automatically)
  addTrackingReducerConnection();

  const trackEvent = async (
    event: 'click' | 'pageview' | 'global',
    eventData: TrackingLinkClickEvent | TrackingPageViewEvent | TrackingGlobalEvent,
    application?: Partial<TrackingApplication>,
  ) => {
    const { b2xSwitch } = config;
    let businessUnit;

    try {
      businessUnit = await waitForCscsUserContext();
    } catch (error) {
      Logger.error('Tracking::trackEvent() -> ', error);
      businessUnit = b2xSwitch.default?.toLowerCase();
    }

    const data: TrackingEvent = {
      ...eventData,
      event,
      application: {
        ...defaultApplication,
        businessUnit,
        ...application,
      },
    };

    Logger.debug('trackEvent', data);

    return aemTrackEvent(data);
  };

  const trackLinkClick = async (eventData: TrackingLinkClickEvent, application?: Partial<TrackingApplication>) => {
    await trackGlobalData();

    return trackEvent('click', eventData, application);
  };

  const trackPageView = async (eventData: TrackingPageViewEvent, trackGlobal = true) => {
    if (trackGlobal) {
      await trackGlobalData();
    }

    return trackEvent('pageview', eventData);
  };

  // We need to send the global tracking event anytime the MQ or user login state has changed.
  // We store the last tracked global data as a JSON string to be able to compare if something changed
  let currentGlobalTrackingDataJson = '';

  const getMediaQueryIdentifier = () => {
    const windowMediaQueryService = new MediaQueryService(window);

    return windowMediaQueryService.getCurrentMediaQuery().toUpperCase();
  };

  const trackGlobalData = async () => {
    const { isAuthenticated } = await authService.getAuthState();

    const eventData: TrackingGlobalEvent = {
      site: {
        market: shopCountry,
        language: lang,
        currency: tracking.currencyIsoCode || currency,
        mediaQuery: getMediaQueryIdentifier(),
        sysEnv: config.apiMode.replace(/[0-9]/g, ''), // remove numbers from dev1, dev2 etc
      },
      user: {
        loginState: isAuthenticated ? 'logged in' : 'logged out',
        customerType: undefined,
      },
    };

    const newGlobalTrackingDataJson = JSON.stringify(eventData);
    const hasChanged = currentGlobalTrackingDataJson !== newGlobalTrackingDataJson;

    if (hasChanged) {
      currentGlobalTrackingDataJson = newGlobalTrackingDataJson;
      await trackEvent('global', eventData);
    }
  };

  return {
    trackLinkClick,
    trackPageView,
  };
};
